import { useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { useLatest } from 'ahooks';
import type { VidSrc } from '@archipro-design/aria';
import {
    useAppDisplayModeContext,
    useStyles,
    VideoPlayer,
} from '@archipro-design/aria';
import { useTracker } from '@archipro-website/tracker';
import useHiResImage from '@modules/product/hook/use-hi-res-image';
import { getThumbnailImage } from '@modules/product/util/get-thumbnail-image';
import type { LibraryVideo } from '@modules/product/type/product-gallery-types';
import * as S from './VideoSlide.style';

interface VideoSlideProps {
    libraryVideo: LibraryVideo;
}

const VideoSlide = ({ libraryVideo }: VideoSlideProps) => {
    const [shouldPlay, setShouldPlay] = useState(false);
    const { css } = useStyles();
    const { mobile } = useAppDisplayModeContext();

    const tracker = useTracker();

    const getImageRef = useLatest(getThumbnailImage);

    const { image } = getImageRef.current(libraryVideo);
    const { VideoID, Type } = libraryVideo;
    const imgSrc = image.Desktop ?? '';

    const { hiResImage, fallback } = useHiResImage(imgSrc);

    const [videoKey, setVideoKey] = useState(0);
    useEffect(() => {
        if (!mobile) {
            setVideoKey((prevKey) => prevKey + 1);
        }
    }, [hiResImage, mobile]);

    const [observerRef, inView] = useInView({
        rootMargin: '50% 0px',
    });
    useEffect(() => {
        if (!inView) {
            setShouldPlay(false);
        }
    }, [inView]);

    const extraVideoProps = {
        playing: shouldPlay,
        muted: true,
        controls: true,
        playsInline: false,
        onPlay: () => {
            setShouldPlay(true);
        },
        trackPlayEvent: () => {
            tracker.log('Video', {
                url: new URL(window.location.href),
            });
        },
    };

    const videoType = Type.toLowerCase() as VidSrc['type'];

    return (
        <div ref={observerRef} className={css(S.VideoContainer)}>
            <VideoPlayer
                key={videoKey}
                className={css(S.VideoPlayer)}
                src={{
                    videoID: VideoID,
                    type: videoType,
                }}
                coverImage={{
                    src: mobile ? imgSrc : hiResImage,
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    objectPosition: 'center',
                    alt: libraryVideo.Title || '',
                    onError: fallback,
                    variables: {
                        placeholderBackgroundColorLight: 'transparent',
                    },
                }}
                {...extraVideoProps}
                variables={{
                    playIconSizeDesktop: 80,
                    playIconSizeMobile: 80,
                }}
                playIcon={mobile ? undefined : <span />}
            />
        </div>
    );
};

export default VideoSlide;
